/**
 * VDB Framework — Shopify Storefront Provider
 * Build 003 / Commit 003.4
 *
 * Storefront API currently provides pagination for metaobjects but no field
 * query argument. Models are therefore fetched once, cached, then filtered
 * locally by their Vehicle Make reference.
 */
(function (window) {
  "use strict";

  const VDB = window.VDB = window.VDB || {};
  const C = window.VDBConfig || {};
  const S = C.shopify || {};
  const metrics = {
    requests: 0,
    cacheHits: 0,
    pages: 0,
    lastRequestMs: null,
    lastError: null,
    makesLoaded: 0,
    modelsLoaded: 0
  };

  function assertConfig() {
    if (!S.shopDomain || !S.storefrontPublicToken || !S.storefrontApiVersion) {
      throw new Error("VDB Shopify Storefront configuration is incomplete.");
    }
  }

  function endpoint() {
    return `https://${S.shopDomain}/api/${S.storefrontApiVersion}/graphql.json`;
  }

  function cacheKey(name) {
    return `vdb.storefront.${C.commit || "dev"}.${name}`;
  }

  function readCache(name) {
    try {
      const raw = localStorage.getItem(cacheKey(name));
      if (!raw) return null;
      const value = JSON.parse(raw);
      if (!value || Date.now() > value.expiresAt) {
        localStorage.removeItem(cacheKey(name));
        return null;
      }
      metrics.cacheHits += 1;
      return value.data;
    } catch (_) {
      return null;
    }
  }

  function writeCache(name, data) {
    try {
      localStorage.setItem(cacheKey(name), JSON.stringify({
        expiresAt: Date.now() + (S.cacheTtlMs || 86400000),
        data
      }));
    } catch (_) {}
  }

  async function graphQL(query, variables) {
    assertConfig();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), C.requestTimeoutMs || 15000);
    const started = performance.now();
    metrics.requests += 1;

    try {
      const response = await fetch(endpoint(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": S.storefrontPublicToken
        },
        body: JSON.stringify({ query, variables }),
        signal: controller.signal
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(`Shopify Storefront API ${response.status}: ${response.statusText}`);
      }
      if (payload.errors?.length) {
        throw new Error(payload.errors.map(error => error.message).join(" | "));
      }
      metrics.lastRequestMs = Math.round(performance.now() - started);
      return payload.data;
    } catch (error) {
      metrics.lastError = error.message;
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  const METAOBJECTS_QUERY = `
    query VdbMetaobjects($type: String!, $first: Int!, $after: String) {
      metaobjects(type: $type, first: $first, after: $after) {
        nodes {
          id
          handle
          type
          updatedAt
          fields {
            key
            type
            value
            reference {
              ... on Metaobject {
                id
                handle
                type
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  async function allMetaobjects(type) {
    const cached = readCache(type);
    if (cached) return cached;

    const nodes = [];
    let after = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const data = await graphQL(METAOBJECTS_QUERY, {
        type,
        first: Math.min(Number(S.pageSize) || 250, 250),
        after
      });
      const connection = data?.metaobjects;
      if (!connection) throw new Error(`Metaobject type "${type}" was not returned.`);
      nodes.push(...connection.nodes);
      metrics.pages += 1;
      hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
      after = connection.pageInfo?.endCursor || null;
    }

    writeCache(type, nodes);
    return nodes;
  }

  function fieldsByKey(node) {
    return Object.fromEntries((node.fields || []).map(field => [field.key, field]));
  }

  function trueValue(field) {
    if (!field) return true;
    return field.value === true || field.value === "true" || field.value === "1";
  }

  function mapMake(node) {
    const f = fieldsByKey(node);
    const keys = S.fields.make;
    return {
      id: f[keys.vdbId]?.value || node.id,
      gid: node.id,
      handle: node.handle,
      value: f[keys.vdbId]?.value || f[keys.code]?.value || node.handle,
      code: f[keys.code]?.value || "",
      name: f[keys.name]?.value || node.handle,
      label: f[keys.name]?.value || node.handle,
      active: trueValue(f[keys.active])
    };
  }

  function mapModel(node) {
    const f = fieldsByKey(node);
    const keys = S.fields.model;
    const makeRef = f[keys.makeReference]?.reference || null;
    return {
      id: f[keys.vdbId]?.value || node.id,
      gid: node.id,
      handle: node.handle,
      value: f[keys.vdbId]?.value || f[keys.code]?.value || node.handle,
      code: f[keys.code]?.value || "",
      name: f[keys.name]?.value || node.handle,
      label: f[keys.name]?.value || node.handle,
      makeName: f[keys.makeName]?.value || "",
      makeGid: makeRef?.id || "",
      makeHandle: makeRef?.handle || "",
      active: trueValue(f[keys.active])
    };
  }

  async function getMakes() {
    const nodes = await allMetaobjects(S.metaobjects.makeType);
    const makes = nodes.map(mapMake)
      .filter(item => item.active)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    metrics.makesLoaded = makes.length;
    return { makes, source: "shopify-storefront", count: makes.length };
  }

  async function getModels(makeIdentifier) {
    const [makeData, nodes] = await Promise.all([
      getMakes(),
      allMetaobjects(S.metaobjects.modelType)
    ]);

    const selectedMake = makeData.makes.find(make =>
      [make.id, make.gid, make.handle, make.code, make.value, make.name]
        .filter(Boolean)
        .some(value => String(value) === String(makeIdentifier))
    );

    if (!selectedMake) {
      return { models: [], source: "shopify-storefront", count: 0 };
    }

    const selectedNames = new Set(
      [selectedMake.name, selectedMake.label].filter(Boolean).map(v => v.toLowerCase())
    );

    const models = nodes.map(mapModel)
      .filter(model => model.active)
      .filter(model =>
        model.makeGid === selectedMake.gid ||
        model.makeHandle === selectedMake.handle ||
        selectedNames.has((model.makeName || "").toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    metrics.modelsLoaded = nodes.length;
    return {
      models,
      make: selectedMake,
      source: "shopify-storefront",
      count: models.length,
      totalModelsCached: nodes.length
    };
  }

  async function health() {
    const started = performance.now();
    try {
      const makes = await getMakes();
      return {
        status: "ok",
        provider: "shopify",
        source: "storefront-api",
        shopDomain: S.shopDomain,
        apiVersion: S.storefrontApiVersion,
        makes: makes.count,
        latencyMs: Math.round(performance.now() - started),
        metrics: { ...metrics }
      };
    } catch (error) {
      return {
        status: "error",
        provider: "shopify",
        source: "storefront-api",
        error: error.message,
        metrics: { ...metrics }
      };
    }
  }


  async function resolutionRequest(path, params) {
    const service = S.resolutionService || {};
    if (!service.baseUrl) {
      throw new Error("VDB Resolution Service is not configured.");
    }

    const url = new URL(`${service.baseUrl}${path}`, window.location.origin);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    });

    const controller = new AbortController();
    const timer = setTimeout(
      () => controller.abort(),
      Number(service.timeoutMs) || Number(C.requestTimeoutMs) || 15000
    );

    try {
      metrics.requests += 1;
      const started = performance.now();
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: { "Accept": "application/json" },
        signal: controller.signal
      });
      const payload = await response.json().catch(() => ({}));
      metrics.lastRequestMs = Math.round(performance.now() - started);

      if (!response.ok || payload.ok === false) {
        throw new Error(payload.error || `VDB Resolution Service ${response.status}`);
      }
      return payload.data ?? payload;
    } catch (error) {
      metrics.lastError = error.message;
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  const provider = {
    name: "shopify",
    health,
    getMakes,
    getModels,
    async getYears(make, model) {
      return resolutionRequest("/years", { make, model });
    },
    async getEngines(make, model, year) {
      return resolutionRequest("/engines", { make, model, year });
    },
    async resolveVehicle(params) {
      return resolutionRequest("/resolve", params || {});
    },
    clearCache() {
      Object.keys(localStorage)
        .filter(key => key.startsWith("vdb.storefront."))
        .forEach(key => localStorage.removeItem(key));
    },
    diagnostics() {
      return {
        config: {
          shopDomain: S.shopDomain,
          apiVersion: S.storefrontApiVersion,
          makeType: S.metaobjects.makeType,
          modelType: S.metaobjects.modelType,
          pageSize: S.pageSize,
          cacheTtlMs: S.cacheTtlMs
        },
        metrics: { ...metrics }
      };
    }
  };

  if (!VDB.providers?.register) {
    throw new Error("VDB provider registry must load before Shopify provider.");
  }
  VDB.providers.register("shopify", provider);
})(window);
